import Container from 'components/helpers/Container';
import { ListingFields, TcDirectoryContactProps, TcDirectoryListingProps } from './_types';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import HeadingTag from 'components/helpers/HeadingTag';
import { ValidHeadingLevels } from 'lib/sitecore/component-params';
import { LayoutTwoColumnShell } from 'components/authorable/layout/LayoutTwoColumn/LayoutTwoColumn';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { classnames } from 'tailwindcss-classnames';
import { LinkField } from '@sitecore-jss/sitecore-jss-react';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';

const linkClasses = classnames(
  'text-blue',
  'underline',
  'font-regular',
  'hover:text-theme-btn-link-text-hover'
);

const Contact = ({ contact }: { contact?: TcDirectoryContactProps }) => {
  if (!contact?.fields) {
    return <></>;
  }
  const contactType =
    contact.fields.type?.displayName === 'Coordinator'
      ? 'Transplant center coordinator'
      : 'Financial representative';
  const contactName = `${contact.fields.first.value} ${contact.fields.last.value}`;
  const ext = contact.fields.ext?.value;
  const phone = !ext ? `${contact.fields.phone.value}` : `${contact.fields.phone.value} ext ${ext}`;
  const email = contact.fields.email.value;
  return (
    <div className={classnames('mb-4')}>
      <div className={classnames('mb-4')}>
        <div className={classnames('mb-2')}>
          <strong>{contactType}</strong>
        </div>
        <div>{contactName}</div>
        {phone && <div>Phone: {phone}</div>}
        {email && (
          <div>
            Email:{' '}
            <LinkA11yWrapper
              className={linkClasses}
              field={{
                value: {
                  href: `mailto:${email}`,
                  text: email,
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const FindContact = ({
  contacts,
  coordinator,
}: {
  contacts?: TcDirectoryContactProps[];
  coordinator?: string;
}) => {
  const noResultsMsg =
    coordinator === 'Coordinator'
      ? "If you have transplant-related questions, please call the transplant center's direct phone number."
      : 'If you have questions about costs and financial services at this center, please call the transplant center’s direct phone number.';
  const searchResults = contacts?.find((v) => {
    return v.fields?.type?.displayName === coordinator;
  }) as TcDirectoryContactProps;
  return searchResults ? (
    <Contact contact={searchResults} />
  ) : (
    <div className={classnames('mb-3')}>{noResultsMsg}</div>
  );
};

const LeftSection = ({ contacts }: { contacts?: TcDirectoryContactProps[] }): React.JSX.Element => (
  <>
    <HeadingTag
      headingLevel={'h3' as ValidHeadingLevels}
      className={TcDirectoryListingClasses.Heading.Medium}
    >
      Transplant center resources
    </HeadingTag>
    <div className={classnames('p-0', 'rte', 'text-left')}>
      {contacts?.length ? (
        <>
          <FindContact contacts={contacts} coordinator="Coordinator" />
          <FindContact contacts={contacts} coordinator="Financial Representative" />
        </>
      ) : (
        <>
          <p>
            If you have transplant-related questions, please call the transplant center&apos;s
            direct phone number.
          </p>
          <p>
            If you have questions about costs and financial services at this center, please call the
            transplant center&apos;s direct phone number.
          </p>
        </>
      )}
    </div>
  </>
);

const RightSection = () => {
  return (
    <>
      <HeadingTag
        headingLevel={'h3' as ValidHeadingLevels}
        className={TcDirectoryListingClasses.Heading.Medium}
      >
        NMDP patient navigators
      </HeadingTag>
      <div className={classnames('p-0', 'rte', 'text-left')}>
        <p>
          Patient navigators can answer your questions about choosing a transplant center and
          provide support and education to help you throughout your transplant journey.
        </p>
        <ul className={classnames('list-disc', 'pl-5')}>
          <li>Inside the United States: 1 (888) 999-6743</li>
          <li>
            Outside the United States: 1 (763) 406-3410. (Long distance or international charges may
            apply.)
          </li>
        </ul>
        <p>
          Email:{' '}
          <LinkA11yWrapper
            className={linkClasses}
            field={{
              value: {
                href: `mailto:patientinfo@nmdp.org`,
                text: 'patientinfo@nmdp.org',
              },
            }}
          />
        </p>
      </div>
    </>
  );
};
const ListingLink = ({ link, appendedText }: { link?: LinkField; appendedText?: string }) => {
  if (!link) {
    return <></>;
  }
  return (
    <li>
      <LinkA11yWrapper className={linkClasses} field={link} /> {appendedText}
    </li>
  );
};
const MoreResources = ({ listing }: { listing?: ListingFields }) => {
  if (!listing) {
    return <></>;
  }
  const {
    chooseCenterLink,
    preparingForTxLink,
    learnOutcomesLink,
    resourcesLink,
    txDataByCenterLink,
  } = listing;

  // Fail out if listing fields are empty
  const listingFieldsEmpty = [
    chooseCenterLink,
    preparingForTxLink,
    learnOutcomesLink,
    resourcesLink,
    txDataByCenterLink,
  ].every((v) => {
    return !v?.value;
  });

  if (listingFieldsEmpty) {
    return <></>;
  }
  return (
    <>
      <HeadingTag
        headingLevel={'h3' as ValidHeadingLevels}
        className={TcDirectoryListingClasses.Heading.Medium}
      >
        More resources
      </HeadingTag>
      <ul>
        <ListingLink link={chooseCenterLink} />
        <ListingLink link={preparingForTxLink} />
        <ListingLink link={learnOutcomesLink} />
        <ListingLink link={resourcesLink} appendedText="(including non-English materials)" />
        <ListingLink link={txDataByCenterLink} appendedText="at blood cell.transplant.hrsa.gov" />
      </ul>
    </>
  );
};

const AdditionalInfoTab = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields) {
    return <MissingDataSource />;
  }
  const { contacts, listing } = fields;
  return (
    <Container size="standard" dataComponent="authorable/nmdpsite/tcdirectory/additional-info">
      <div className={classnames('mb-10', 'lg:mb-6')}>
        <LayoutTwoColumnShell left={<LeftSection contacts={contacts} />} right={<RightSection />} />
      </div>
      <div className={classnames('mb-6')}>
        <MoreResources listing={listing} />
      </div>
    </Container>
  );
};

export default AdditionalInfoTab;
